import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import styles from './BirdReproductionPeriod.module.css';

function BirdReproductionPeriod() {
    const navigate = useNavigate();

    const [birds, setBirds] = useState([]);
    const [selectedBird, setSelectedBird] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Novos estados de filtro para a busca de aves
    const [genderFilter, setGenderFilter] = useState('all'); // 'all', 'male', 'female'
    const [searchTerm, setSearchTerm] = useState('');

    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Estados para controlar quais reproduções e posturas estão abertas
    const [openReproductions, setOpenReproductions] = useState({});
    const [openPostures, setOpenPostures] = useState({});

    useEffect(() => {
        async function fetchBirds() {
            try {
                const token = JSON.parse(localStorage.getItem('token'));
                const res = await api.get('/bird/mybirds', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setBirds(res.data.birds || []);
            } catch (err) {
                console.error('Erro ao buscar aves:', err);
            }
        }
        fetchBirds();
    }, []);

    // Filtra a lista de aves com base no gênero e no termo digitado (Nome, Raça/Espécie, Cor, Anilhas)
    const filteredBirds = useMemo(() => {
        return birds.filter((bird) => {
            // Filtro por gênero
            if (genderFilter !== 'all' && bird.gender !== genderFilter) {
                return false;
            }

            // Filtro por texto
            if (searchTerm.trim() !== '') {
                const term = searchTerm.toLowerCase();
                const nameMatch = bird.name?.toLowerCase().includes(term);
                const breedMatch = bird.breed?.toLowerCase().includes(term) || bird.species?.toLowerCase().includes(term);
                const colorMatch = bird.color?.toLowerCase().includes(term);
                const bandLeftMatch = bird.bird_band_left?.toLowerCase().includes(term);
                const bandRightMatch = bird.bird_band_right?.toLowerCase().includes(term);

                return nameMatch || breedMatch || colorMatch || bandLeftMatch || bandRightMatch;
            }

            return true;
        });
    }, [birds, genderFilter, searchTerm]);

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!selectedBird) {
            setError('Selecione uma ave para gerar o relatório.');
            return;
        }

        setError('');
        setLoading(true);
        setReportData(null);
        setOpenReproductions({});
        setOpenPostures({});

        try {
            const token = JSON.parse(localStorage.getItem('token'));
            const queryParams = new URLSearchParams();
            if (startDate) queryParams.append('startDate', startDate);
            if (endDate) queryParams.append('endDate', endDate);

            const res = await api.get(`/report/reproductions/${selectedBird}?${queryParams.toString()}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setReportData(res.data);
        } catch (err) {
            console.error('Erro ao buscar relatório:', err);
            setError(err.response?.data?.message || 'Erro ao gerar relatório.');
        } finally {
            setLoading(false);
        }
    };

    const toggleReproduction = (reproId) => {
        setOpenReproductions(prev => ({ ...prev, [reproId]: !prev[reproId] }));
    };

    const togglePosture = (postureId) => {
        setOpenPostures(prev => ({ ...prev, [postureId]: !prev[postureId] }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Em Andamento / Não informada';
        return new Date(dateString).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    };

    return (
        <section className={styles.container}>
            <button className={styles.btn_back} onClick={() => navigate('/report')}>
                ← Voltar para Relatórios
            </button>

            <div className={styles.header}>
                <h2>📊 Relatório de Reprodução por Ave</h2>
                <p>Filtre por uma ave e um intervalo de datas para analisar o rendimento reprodutivo.</p>
            </div>

            <form onSubmit={handleSearch} className={styles.filter_card}>
                {/* FILTROS DE SELEÇÃO DA AVE */}
                <div className={styles.form_group}>
                    <label>Filtrar por Gênero:</label>
                    <select 
                        value={genderFilter} 
                        onChange={(e) => {
                            setGenderFilter(e.target.value);
                            setSelectedBird(''); // Limpa a ave selecionada ao mudar o filtro
                        }}
                    >
                        <option value="all">Todos os Gêneros</option>
                        <option value="male">Machos (♂)</option>
                        <option value="female">Fêmeas (♀)</option>
                    </select>
                </div>

                <div className={styles.form_group}>
                    <label>Pesquisar Ave (Nome, Raça, Cor, Anilha):</label>
                    <input 
                        type="text" 
                        placeholder="Ex: Amarelo, Canário, 123..." 
                        value={searchTerm} 
                        onChange={(e) => setSearchTerm(e.target.value)} 
                    />
                </div>

                <div className={styles.form_group}>
                    <label>Selecione a Ave ({filteredBirds.length}):</label>
                    <select 
                        value={selectedBird} 
                        onChange={(e) => setSelectedBird(e.target.value)}
                        required
                    >
                        <option value="">Selecione uma Ave</option>
                        {filteredBirds.map((b) => (
                            <option key={b._id} value={b._id}>
                                {b.name || 'Sem Nome'} - {b.race || 'Sem raça'} - {b.color || 'Sem cor'} [{b.bird_band_left || '-'}/{b.bird_band_right || '-'}]
                            </option>
                        ))}
                    </select>
                </div>

                {/* FILTROS DE DATAS */}
                <div className={styles.form_group}>
                    <label>Data Inicial:</label>
                    <input 
                        type="date" 
                        value={startDate} 
                        onChange={(e) => setStartDate(e.target.value)} 
                    />
                </div>

                <div className={styles.form_group}>
                    <label>Data Final:</label>
                    <input 
                        type="date" 
                        value={endDate} 
                        onChange={(e) => setEndDate(e.target.value)} 
                    />
                </div>

                <button type="submit" className={styles.btn_submit} disabled={loading}>
                    {loading ? 'Gerando...' : 'Gerar Relatório'}
                </button>
            </form>

            {error && <p className={styles.error_msg}>{error}</p>}

            {reportData && (
                <div className={styles.result_container}>
                    <div className={styles.bird_info_banner}>
                        <h3>Ave: {reportData.bird.name}</h3>
                        <p>
                            <b>Gênero:</b> {reportData.bird.gender === 'male' ? <span className={styles.male_icon}>♂</span> : <span className={styles.female_icon}>♀</span>} | <b>Anilhas:</b> {reportData.bird.bands}
                        </p>
                        <small><b>Período:</b> {reportData.period.startDate} até {reportData.period.endDate}</small>
                    </div>

                    {/* Cálculo do aproveitamento */}
                    {(() => {
                        const totalEggs = reportData.summary.eggMetrics.totalEggs || 0;
                        const offspringCount = reportData.summary.offspringCount || 0;
                        const hatchingRate = totalEggs > 0 
                            ? ((offspringCount / totalEggs) * 100).toFixed(1) 
                            : '0.0';

                        return (
                            <div className={styles.metrics_grid}>
                                <div className={styles.metric_card}>
                                    <span>🔄 Reproduções</span>
                                    <strong>{reportData.summary.totalReproductions}</strong>
                                </div>

                                <div className={styles.metric_card}>
                                    <span>🪺 Posturas</span>
                                    <strong>{reportData.summary.totalPostures}</strong>
                                </div>

                                <div className={styles.metric_card}>
                                    <span>🥚 Total de Ovos</span>
                                    <strong>{totalEggs}</strong>
                                </div>

                                <div className={`${styles.metric_card} ${styles.highlight}`}>
                                    <span>🐣 Total Filhotes</span>
                                    <strong>{offspringCount}</strong>
                                </div>

                                <div className={styles.metric_card}>
                                    <span>📈 Aproveitamento</span>
                                    <strong>{hatchingRate}%</strong>
                                </div>
                            </div>
                        );
                    })()}

                    {/* LISTA CLICÁVEL DE REPRODUÇÕES E POSTURAS */}
                    <div className={styles.reproductions_section}>
                        <h3>📂 Reproduções e Posturas Detalhadas</h3>

                        {reportData.reproductions.length > 0 ? (
                            reportData.reproductions.map((repro, rIdx) => {
                                const isReproOpen = !!openReproductions[repro._id];

                                return (
                                    <div key={repro._id} className={styles.repro_accordion}>
                                        <div 
                                            className={styles.repro_header} 
                                            onClick={() => toggleReproduction(repro._id)}
                                        >
                                            <div className={styles.repro_title}>
                                                <span className={styles.badge}>
                                                    <strong>Reprodução #{rIdx + 1} </strong>
                                                     - {repro.cage ? `${repro.cage.title} (Nº ${repro.cage.number})` : 'N/A'}
                                                </span>
                                            </div>
                                            <div className={styles.repro_meta}>
                                                <small>📅 {formatDate(repro.reproduction_start)} até {formatDate(repro.reproduction_end)}</small>
                                                <span className={styles.arrow}>{isReproOpen ? '▲' : '▼'}</span>
                                            </div>
                                        </div>

                                        {isReproOpen && (
                                            <div className={styles.repro_body}>
                                                <h4>🪺 Posturas desta Reprodução ({repro.postures.length})</h4>
                                                
                                                {repro.postures.map((posture, pIdx) => {
                                                    const isPostureOpen = !!openPostures[posture._id];
                                                    const offspringCount = posture.offspring?.length || 0;

                                                    return (
                                                        <div key={posture._id} className={styles.posture_accordion}>
                                                            <div 
                                                                className={styles.posture_header} 
                                                                onClick={() => togglePosture(posture._id)}
                                                            >
                                                                <div>
                                                                    <strong>Postura #{pIdx + 1}</strong>
                                                                    <span> — 🥚 Ovos: <b>{posture.number_eggs || 0}</b> | 🐣 Filhotes: <b>{offspringCount}</b></span>
                                                                </div>
                                                                <span className={styles.arrow}>{isPostureOpen ? '▲' : '▼'}</span>
                                                            </div>

                                                            {isPostureOpen && (
                                                                <div className={styles.posture_body}>
                                                                    <div className={styles.posture_info}>
                                                                        <p>
                                                                            <span className={styles.male_icon} title="Macho">♂</span> <b>{posture.male?.name || 'N/A'}</b> ({posture.male?.bird_band_left || '-'}/{posture.male?.bird_band_right || '-'})
                                                                        </p>
                                                                        <p>
                                                                            <span className={styles.female_icon} title="Fêmea">♀</span> <b>{posture.female?.name || 'N/A'}</b> ({posture.female?.bird_band_left || '-'}/{posture.female?.bird_band_right || '-'})
                                                                        </p>
                                                                        <p>📅 <b>Início:</b> {formatDate(posture.posture_start)}</p>
                                                                        <p>📅 <b>Fim:</b> {formatDate(posture.posture_end)}</p>
                                                                    </div>

                                                                    <div className={styles.offspring_list_wrapper}>
                                                                        <h5>🐣 Filhotes da Postura ({offspringCount})</h5>
                                                                        {offspringCount > 0 ? (
                                                                            <div className={styles.offspring_grid}>
                                                                                {posture.offspring.map((child) => (
                                                                                    <div key={child._id} className={styles.offspring_card}>
                                                                                        <div className={styles.offspring_header}>
                                                                                            {child.gender === 'female' ? (
                                                                                                <span className={styles.female_icon} title="Fêmea">♀</span>
                                                                                            ) : child.gender === 'male' ? (
                                                                                                <span className={styles.male_icon} title="Macho">♂</span>
                                                                                            ) : (
                                                                                                <span className={styles.unknown_icon} title="Indefinido" style={{ color: '#000' }}>? </span>
                                                                                            )}
                                                                                            {child.name}
                                                                                        </div>
                                                                                        <p>{child.race} - {child.color || '-'}</p>
                                                                                        <p>Anilhas: {child.bird_band_left || '-'}/{child.bird_band_right || '-'}</p>
                                                                                        <p>Nascimento: {formatDate(child.birthday)}</p>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        ) : (
                                                                            <p className={styles.empty_txt}>Nenhum filhote nascido nesta postura.</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            <p className={styles.empty_txt}>Nenhuma reprodução/postura encontrada para este período.</p>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}

export default BirdReproductionPeriod;